'use client';

import { FC, useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  Text,
  Flex,
  Icon,
  Select,
  Textarea,
  Button,
  Input,
  Dropdown,
} from '@/_components';
import { PromptTemplatesDialog } from '@/_components/PromptTemplatesDialog/PromptTemplatesDialog';
import type { ResultListItem } from '@/_components/ResultList/ResultList.types';
import type {
  WorkflowDialogProps,
  TriggerOption,
  TriggerOptionValue,
} from './WorkflowDialog.types';
import type { WorkflowParams } from '@/_api/admin-workflows';
import styles from './WorkflowDialog.module.css';

const triggerOptions: TriggerOption[] = [
  {
    name: 'When a questionnaire is created',
    value: 'questionnaire_created',
  },
  {
    name: 'When a questionnaire is exported',
    value: 'questionnaire_exported',
  },
  {
    name: 'When a questionnaire is locked',
    value: 'questionnaire_locked',
  },
  {
    name: 'When a questionnaire is submitted',
    value: 'questionnaire_submitted',
  },
  {
    name: 'When a questionnaire comment is added',
    value: 'questionnaire_comment_added',
  },
  { name: 'When a client is added', value: 'client_added' },
  { name: 'When a client is updated', value: 'client_updated' },
  { name: 'When a document is uploaded', value: 'document_uploaded' },
  { name: 'When a document is deleted', value: 'document_deleted' },
  { name: 'When an entity is added', value: 'entity_added' },
  { name: 'When an entity is updated', value: 'entity_updated' },
  { name: 'When a message is sent', value: 'message_sent' },
];

const agentMenuItems: ResultListItem[] = [
  { value: 'prompt-templates', name: 'Prompt templates', icon: 'cards' },
];

export const WorkflowDialog: FC<WorkflowDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  workflowToEdit,
}) => {
  const defaultTitle = 'Soraban → Karbon Workflow';
  const initialSorabanAgentContent = `Add a work in Karbon with the following information:
- Title: Review {questionnaire.name}
- Client: {questionnaire.company.name}
- Type: Accounting
- Assigned to: Jimmy Loi
`;
  const [workflowTitle, setWorkflowTitle] = useState<string>(defaultTitle);
  const [selectedTrigger, setSelectedTrigger] = useState<TriggerOptionValue>(
    triggerOptions[0].value,
  );
  const [sorabanAgentContent, setSorabanAgentContent] = useState<string>(
    initialSorabanAgentContent,
  );
  const [isPromptTemplatesDialogOpen, setIsPromptTemplatesDialogOpen] =
    useState(false);

  const togglePromptTemplatesDialog = useCallback(() => {
    setIsPromptTemplatesDialogOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (workflowToEdit) {
      setWorkflowTitle(workflowToEdit.title || defaultTitle);
      // Assuming the first step is trigger and second is soraban_agent
      // This might need to be more robust if step order or types can vary significantly
      const triggerStep = workflowToEdit.workflow_steps?.find(
        (step) => step.step_type === 'trigger',
      );
      if (triggerStep && triggerStep.content?.type) {
        setSelectedTrigger(triggerStep.content.type as TriggerOptionValue);
      } else {
        setSelectedTrigger(triggerOptions[0].value);
      }

      const agentStep = workflowToEdit.workflow_steps?.find(
        (step) => step.step_type === 'soraban_agent',
      );
      if (agentStep && agentStep.content?.task) {
        setSorabanAgentContent(agentStep.content.task as string);
      } else {
        setSorabanAgentContent(initialSorabanAgentContent);
      }
    } else {
      setWorkflowTitle(defaultTitle);
      setSelectedTrigger(triggerOptions[0].value);
      setSorabanAgentContent(initialSorabanAgentContent);
    }
  }, [workflowToEdit, isOpen, initialSorabanAgentContent]);

  const handleTriggerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTrigger(e.target.value as TriggerOptionValue);
  };

  const handleSave = useCallback(() => {
    const workflowData: WorkflowParams = {
      title: workflowTitle,
      steps: [
        {
          id: workflowToEdit?.workflow_steps?.[0]?.id || undefined,
          stepType: 'trigger',
          content: { type: selectedTrigger },
          position: 1,
        },
        {
          id: workflowToEdit?.workflow_steps?.[1]?.id || undefined,
          stepType: 'soraban_agent',
          content: { task: sorabanAgentContent },
          position: 2,
        },
      ],
    };
    onSave(workflowData);
  }, [
    onSave,
    workflowTitle,
    selectedTrigger,
    sorabanAgentContent,
    workflowToEdit,
  ]);

  const handleAgentMenuSelect = useCallback(
    (item: ResultListItem) => {
      if (item.value === 'prompt-templates') {
        togglePromptTemplatesDialog();
      } else {
        // Placeholder action: Log to console.
        // Actual implementation for each item will be done in subsequent tasks.
        console.log(
          `Selected agent menu item: ${item.label} (name: ${item.name})`,
        );
      }
    },
    [togglePromptTemplatesDialog],
  );

  return (
    <>
      <Dialog
        isFullScreen
        isOpen={isOpen}
        onClose={onClose}
        title={workflowToEdit ? 'Edit Workflow' : 'Create Workflow'}
        className={styles.dialog}
        headerButtons={
          <Button value="Save" onClick={handleSave} intent="primary" />
        }
      >
        <Flex isColumn gap={6} className={styles.dialogContent}>
          <Flex xAlign="start">
            <Input
              className={styles.workflowTitleInput}
              label="Workflow Title"
              isLabelHidden
              value={workflowTitle}
              onChange={(e) => setWorkflowTitle(e.target.value)}
              placeholder="Enter workflow title"
            />
          </Flex>

          <Flex
            className={styles.dialogInnerContent}
            isXCentered
            isYCentered
            gap={6}
            isWrapping={false}
          >
            {/* Trigger Box */}
            <Flex isColumn className={styles.boxContainer}>
              <Flex isRow gap={2} className={styles.boxHeader}>
                <Icon name="lightning" color="secondary" size={16} />
                <Text fontWeight="bold">Trigger</Text>
              </Flex>

              <Flex isColumn className={styles.boxContent}>
                <Select
                  label="Trigger selection"
                  isLabelHidden
                  isFullWidth
                  items={triggerOptions}
                  selectedValue={
                    triggerOptions.find((opt) => opt.value === selectedTrigger)
                      ?.value
                  }
                  onChange={handleTriggerChange}
                  placeholder="Select a trigger"
                />
              </Flex>
            </Flex>

            {/* Arrow */}
            <Text className={styles.arrow}>→</Text>

            {/* Soraban Agent Box */}
            <Flex isColumn className={styles.boxContainer}>
              <Flex
                isRow
                gap={2}
                className={styles.boxHeader}
                isBetween
                isYCentered
              >
                <Flex isRow gap={2} xAlign="center">
                  <Icon name="chat-teardrop-dots" color="secondary" size={16} />
                  <Text fontWeight="bold">Soraban Agent</Text>
                </Flex>
                <Dropdown
                  items={agentMenuItems}
                  onSelect={handleAgentMenuSelect}
                >
                  <Button
                    iconLeft="dots-three-outline"
                    onClick={() => {}}
                    size="small"
                    isIconOnly={true}
                    withTooltip={true}
                    intent="ghost"
                    value="Settings"
                  />
                </Dropdown>
              </Flex>

              <Flex isColumn className={styles.boxContent}>
                <Textarea
                  label="Soraban Agent Configuration"
                  isLabelHidden
                  value={sorabanAgentContent}
                  onChange={(e) => setSorabanAgentContent(e.target.value)}
                  rows={4}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Dialog>
      {isPromptTemplatesDialogOpen && (
        <PromptTemplatesDialog
          isOpen={isPromptTemplatesDialogOpen}
          onClose={togglePromptTemplatesDialog}
          // onSelectTemplate={(template) => {
          //   // Handle selected template, e.g., append to sorabanAgentContent
          //   console.log('Template selected in WorkflowDialog:', template);
          //   // setSorabanAgentContent(prev => `${prev}\n\n${template.title}`);
          //   togglePromptTemplatesDialog();
          // }}
        />
      )}
    </>
  );
};
